import { toNullableBoolean, toNullableNumber, toNullableString } from '~/utils/coerce-nullable';
import { AI_VALIDATION_API_LMI_URL } from '~/utils/constants';
import { store } from '~/utils/store';
import { getLmiDocumentValidationMessage, LmiDocumentValidationMessageKey } from './messages';

export enum LmiQualificationStatus {
  Qualified = 'Qualified',
  NeedsReview = 'Needs Review',
  Failed = 'Failed',
  Empty = '',
}

export enum LmiViolationSeverity {
  Error = 'error',
  Warning = 'warning',
}

export interface ValidateLmiDocumentPage {
  readonly file: File;
}

export interface ValidateLmiDocumentParams {
  readonly accountId?: number;
  readonly documentType: string;
  readonly funnelId?: string;
  readonly nameOnDocument: string;
  readonly page: ValidateLmiDocumentPage;
  readonly qualifyingProgram: string;
  readonly relationship: string;
  readonly userId?: number;
  readonly utilityAccountId?: number;
  readonly utilityId: number;
  readonly validationRuleset?: 'default' | 'mn-state-lmi';
}

export interface LmiDocumentValidationViolation {
  readonly messageKey?: string | null;
  readonly severity?: LmiViolationSeverity | null;
  readonly field?: string | null;
  readonly message?: string | null;
  readonly subtitle?: {
    readonly en?: string | null;
    readonly es?: string | null;
  } | null;
}

export interface LmiDocumentExtractionData {
  readonly customerName?: string | null;
  readonly nameMatchesExpected?: boolean | null;
  readonly nameMatchReason?: string | null;
  readonly documentDate?: string | null;
  readonly documentTypeKey?: string | null;
  readonly documentTypeLabel?: string | null;
  readonly currentEnrollmentVerified?: boolean | null;
  readonly manualReviewRequired?: boolean | null;
  readonly notes?: string | null;
  readonly programMatchVerified?: boolean | null;
  readonly validDate?: boolean | null;
  readonly validDateReason?: string | null;
  readonly verificationEvidence?: string | null;
  readonly verificationFailureReason?: string | null;
  readonly visualQualityRating?: number | null;
  readonly visualQualityReason?: string | null;
}

export interface ValidateLmiDocumentResponse {
  readonly extractionData?: LmiDocumentExtractionData;
  readonly messageKey: LmiDocumentValidationMessageKey;
  readonly qualificationStatus?: LmiQualificationStatus | null;
  readonly violations: readonly LmiDocumentValidationViolation[];
}

const VALIDATION_MESSAGE_KEYS = Object.values(LmiDocumentValidationMessageKey);

const QUALIFICATION_STATUS_KEY = 'qualification_status';

const apiTranslationKeyMessageMap: Record<string, string> = {
  AUTHORIZATION_INVALID: LmiDocumentValidationMessageKey.ValidationUnavailable,
  AUTHORIZATION_REQUIRED: LmiDocumentValidationMessageKey.ValidationUnavailable,
  BAD_REQUEST: LmiDocumentValidationMessageKey.ValidationUnavailable,
  FILE_REQUIRED: LmiDocumentValidationMessageKey.ValidationUnavailable,
  INTERNAL_SERVER_ERROR: LmiDocumentValidationMessageKey.ValidationUnavailable,
  INVALID_FILE: 'unsupported_document',
  INVALID_FILE_TYPE: 'unsupported_document',
  LIMIT_FILE_COUNT: 'unsupported_document',
  LIMIT_FILE_SIZE: 'unsupported_document',
  MISSING_FIELD_NAME: LmiDocumentValidationMessageKey.ValidationUnavailable,
  UNKNOWN_FILE_TYPE: 'unsupported_document',
};

const appendOptionalFormField = (
  formData: FormData,
  fieldName: string,
  value: number | string | undefined,
): void => {
  if (value !== undefined) {
    formData.append(fieldName, String(value));
  }
};

const toFormData = ({
  accountId,
  documentType,
  funnelId,
  nameOnDocument,
  page,
  qualifyingProgram,
  relationship,
  userId,
  utilityAccountId,
  utilityId,
  validationRuleset,
}: ValidateLmiDocumentParams): FormData => {
  const formData = new FormData();

  appendOptionalFormField(formData, 'accountId', accountId);
  appendOptionalFormField(formData, 'funnelId', funnelId);
  appendOptionalFormField(formData, 'userId', userId);
  appendOptionalFormField(formData, 'utilityAccountId', utilityAccountId);
  appendOptionalFormField(formData, 'utilityId', utilityId);
  appendOptionalFormField(formData, 'validationRuleset', validationRuleset);
  formData.append('documentType', documentType);
  formData.append('nameOnDocument', nameOnDocument);
  formData.append('qualifyingProgram', qualifyingProgram);
  formData.append('relationship', relationship);
  formData.append('files', page.file, page.file.name);

  return formData;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toApiResults = (value: unknown): unknown => {
  if (!isRecord(value)) return value;
  return isRecord(value.results) ? value.results : value;
};

const toViolations = (value: unknown): readonly LmiDocumentValidationViolation[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(isRecord).map(violation => ({
    field: toNullableString(violation.field),
    message: toNullableString(violation.message),
    messageKey: toNullableString(violation.messageKey) ?? toNullableString(violation.issue),
    severity:
      violation.severity === LmiViolationSeverity.Error ||
      violation.severity === LmiViolationSeverity.Warning
        ? violation.severity
        : undefined,
    subtitle: isRecord(violation.subtitle)
      ? {
          en: toNullableString(violation.subtitle.en),
          es: toNullableString(violation.subtitle.es),
        }
      : undefined,
  }));
};

const toQualificationStatus = (
  value: unknown,
): ValidateLmiDocumentResponse['qualificationStatus'] => {
  if (
    value === LmiQualificationStatus.Qualified ||
    value === LmiQualificationStatus.NeedsReview ||
    value === LmiQualificationStatus.Failed ||
    value === LmiQualificationStatus.Empty ||
    value === null
  ) {
    return value;
  }

  return undefined;
};

const toExtractionData = (value: unknown): LmiDocumentExtractionData | undefined => {
  if (!isRecord(value)) return undefined;

  return {
    customerName: toNullableString(value.customerName),
    nameMatchesExpected: toNullableBoolean(value.nameMatchesExpected),
    nameMatchReason: toNullableString(value.nameMatchReason),
    documentDate: toNullableString(value.documentDate),
    documentTypeKey: toNullableString(value.documentTypeKey),
    documentTypeLabel: toNullableString(value.documentTypeLabel),
    currentEnrollmentVerified: toNullableBoolean(value.currentEnrollmentVerified),
    manualReviewRequired: toNullableBoolean(value.manualReviewRequired),
    notes: toNullableString(value.notes),
    programMatchVerified: toNullableBoolean(value.programMatchVerified),
    validDate: toNullableBoolean(value.validDate),
    validDateReason: toNullableString(value.validDateReason),
    verificationEvidence: toNullableString(value.verificationEvidence),
    verificationFailureReason: toNullableString(value.verificationFailureReason),
    visualQualityRating: toNullableNumber(value.visualQualityRating),
    visualQualityReason: toNullableString(value.visualQualityReason),
  };
};

const isLmiDocumentValidationMessageKey = (key: string): key is LmiDocumentValidationMessageKey =>
  VALIDATION_MESSAGE_KEYS.includes(key as LmiDocumentValidationMessageKey);

const mapExternalMessageKey = (key: string): string => {
  if (key === 'unsupported_document' || key === 'bad_bill') return 'invalid_document';
  if (key === 'missing_required_data') return 'invalid_data';
  return key;
};

const PREFERRED_ERROR_MESSAGE_KEYS: readonly LmiDocumentValidationMessageKey[] = [
  LmiDocumentValidationMessageKey.PoorImageQuality,
];

const findPrimaryViolation = (
  violations: readonly LmiDocumentValidationViolation[],
): LmiDocumentValidationViolation | undefined => {
  const errorViolations = violations.filter(
    violation => violation.severity === LmiViolationSeverity.Error,
  );

  for (const preferredKey of PREFERRED_ERROR_MESSAGE_KEYS) {
    const preferredViolation = errorViolations.find(
      violation => mapExternalMessageKey(violation.messageKey ?? '') === preferredKey,
    );
    if (preferredViolation) return preferredViolation;
  }

  if (errorViolations[0]) return errorViolations[0];

  return (
    violations.find(violation => violation.severity === LmiViolationSeverity.Warning) ??
    violations.find(violation => violation.messageKey)
  );
};

const normalizeMessageKey = (
  violations: readonly LmiDocumentValidationViolation[],
  qualificationStatus: ValidateLmiDocumentResponse['qualificationStatus'],
): LmiDocumentValidationMessageKey => {
  const primaryViolation = findPrimaryViolation(violations);
  if (primaryViolation?.messageKey) {
    const mappedKey = mapExternalMessageKey(primaryViolation.messageKey);
    if (isLmiDocumentValidationMessageKey(mappedKey)) return mappedKey;
  }

  if (qualificationStatus === LmiQualificationStatus.Qualified) {
    return LmiDocumentValidationMessageKey.DocumentValid;
  }

  return LmiDocumentValidationMessageKey.ValidationUnavailable;
};

export const getPrimaryLmiViolation = (
  response: ValidateLmiDocumentResponse,
): LmiDocumentValidationViolation | undefined => findPrimaryViolation(response.violations);

const toHttpErrorViolations = (value: unknown): readonly LmiDocumentValidationViolation[] => {
  if (!isRecord(value)) {
    return [
      {
        messageKey: LmiDocumentValidationMessageKey.ValidationUnavailable,
        severity: LmiViolationSeverity.Warning,
      },
    ];
  }

  const messageKeys = [
    apiTranslationKeyMessageMap[toNullableString(value.translationKey) ?? ''],
    ...(Array.isArray(value.validationErrors)
      ? value.validationErrors
          .filter(isRecord)
          .map(error => apiTranslationKeyMessageMap[toNullableString(error.translationKey) ?? ''])
      : []),
  ].filter((key): key is string => !!key);

  const uniqueKeys = Array.from(
    new Set(
      messageKeys.length > 0
        ? messageKeys
        : [LmiDocumentValidationMessageKey.ValidationUnavailable],
    ),
  );

  return uniqueKeys.map(messageKey => ({
    messageKey,
    message: toNullableString(value.message),
    severity:
      messageKey === 'unsupported_document'
        ? LmiViolationSeverity.Error
        : LmiViolationSeverity.Warning,
  }));
};

const toValidationResponse = (value: unknown): ValidateLmiDocumentResponse => {
  const results = toApiResults(value);
  if (!isRecord(results)) {
    return {
      messageKey: LmiDocumentValidationMessageKey.ValidationUnavailable,
      violations: [],
    };
  }

  const validationResults = isRecord(results.validation_results)
    ? results.validation_results
    : undefined;
  const violations = toViolations(results.violations ?? validationResults?.violations);
  const qualificationStatus = toQualificationStatus(
    results.qualificationStatus ??
      results[QUALIFICATION_STATUS_KEY] ??
      validationResults?.qualification_status,
  );

  return {
    extractionData: toExtractionData(results.extractionData ?? results.extraction_data),
    messageKey: normalizeMessageKey(violations, qualificationStatus),
    qualificationStatus,
    violations,
  };
};

export const validateLmiDocument = async ({
  accountId,
  documentType,
  funnelId,
  nameOnDocument,
  page,
  qualifyingProgram,
  relationship,
  userId,
  utilityAccountId,
  utilityId,
  validationRuleset,
}: ValidateLmiDocumentParams): Promise<ValidateLmiDocumentResponse> => {
  if (!AI_VALIDATION_API_LMI_URL) {
    throw new Error('LMI document validation API URL is not configured.');
  }

  const token = store.get('TOKEN');
  const body = toFormData({
    accountId,
    documentType,
    funnelId,
    nameOnDocument,
    page,
    qualifyingProgram,
    relationship,
    userId,
    utilityAccountId,
    utilityId,
    validationRuleset,
  });

  const response = await fetch(AI_VALIDATION_API_LMI_URL, {
    body,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    method: 'POST',
  });
  const payload = (await response.json()) as unknown;

  if (!response.ok) {
    const violations = toHttpErrorViolations(payload);
    return {
      messageKey: normalizeMessageKey(violations, undefined),
      violations,
    };
  }

  return toValidationResponse(payload);
};

export { getLmiDocumentValidationMessage, LmiDocumentValidationMessageKey };
